import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material';
import { SettingsBackupRestore, SwapHoriz, Warning } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { v2 } from '../../../../services/api';
import { useAuth } from '../../../../contexts/AuthContext';

// Admin-only cutover button: opens a dialog that first shows a DRY RUN of the
// v1 → v2 migration (nothing written), then lets the admin execute it for
// real. Execution cancels every active v1 work order (releasing its component
// reservations) and explodes each eligible order line's remaining shortage
// (ordered − shipped − allocated) into a v2 tree.

const SKIP_REASON_LABELS = {
  nothing_to_produce: 'Fully covered by stock/shipping',
  already_exploded: 'Already in V2',
  not_manufacturable: 'Not manufacturable (no BOM)',
};

function useOptionalAuth() {
  // useAuth throws outside an AuthProvider (e.g. bare unit-test renders);
  // treat that as "no user" so the button simply doesn't render.
  try {
    return useAuth();
  } catch (_) {
    return null;
  }
}

function SkipBreakdown({ linesSkipped }) {
  const byReason = {};
  for (const l of linesSkipped) {
    byReason[l.reason] = (byReason[l.reason] || 0) + 1;
  }
  const entries = Object.entries(byReason);
  if (entries.length === 0) return null;
  return (
    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
      {entries.map(([reason, count]) => (
        <Chip
          key={reason}
          size="small"
          label={`${SKIP_REASON_LABELS[reason] || reason}: ${count}`}
          data-testid={`migrate-skip-${reason}`}
        />
      ))}
    </Stack>
  );
}

function ReportSummary({ report }) {
  return (
    <Box data-testid="migrate-report">
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Chip size="small" color="primary" label={`${report.orders_scanned} orders scanned`} />
        <Chip size="small" color="success" label={`${report.lines_exploded.length} lines to make`} data-testid="migrate-lines-count" />
        <Chip size="small" label={`${report.lines_skipped.length} lines skipped`} />
        <Chip
          size="small"
          color={report.v1_work_orders_cancelled.length > 0 ? 'warning' : 'default'}
          label={`${report.v1_work_orders_cancelled.length} V1 work orders to cancel`}
          data-testid="migrate-v1-cancel-count"
        />
        <Chip size="small" label={`${report.v1_reservations_released} reservations released`} />
      </Stack>

      <SkipBreakdown linesSkipped={report.lines_skipped} />

      {report.lines_exploded.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Work to be created in V2
          </Typography>
          <Table size="small" data-testid="migrate-lines-table">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Line</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Qty to make</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.lines_exploded.map((l) => (
                <TableRow key={l.line_id}>
                  <TableCell>{l.order_number}</TableCell>
                  <TableCell>{l.line_number}</TableCell>
                  <TableCell>{l.product_code}</TableCell>
                  <TableCell align="right">{l.shortage}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {report.v1_work_orders_cancelled.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Active V1 work orders that will be cancelled
          </Typography>
          <Table size="small" data-testid="migrate-v1-table">
            <TableHead>
              <TableRow>
                <TableCell>WO #</TableCell>
                <TableCell>Item</TableCell>
                <TableCell align="right">Outstanding qty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.v1_work_orders_cancelled.map((w) => (
                <TableRow key={w.work_order_number}>
                  <TableCell>{w.work_order_number}</TableCell>
                  <TableCell>{w.output_item_code}</TableCell>
                  <TableCell align="right">{w.quantity_outstanding}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
}

export default function MigrateFromV1Button({ onMigrated, onUndone }) {
  const auth = useOptionalAuth();
  const user = auth?.user;
  const isAdmin = Boolean(user?.is_staff || user?.role === 'admin');

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [report, setReport] = useState(null);   // dry-run report
  const [result, setResult] = useState(null);   // execute report
  const [error, setError] = useState(null);

  const [undoConfirmOpen, setUndoConfirmOpen] = useState(false);
  const [undoing, setUndoing] = useState(false);

  if (!isAdmin) return null;

  const runDryRun = async () => {
    setLoading(true);
    setError(null);
    setReport(null);
    setResult(null);
    try {
      const res = await v2.admin.migrateFromV1({ dry_run: true });
      setReport(res.data);
    } catch (err) {
      console.error('Migration dry run failed', err);
      setError(err.response?.data?.error || 'Dry run failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    runDryRun();
  };

  const handleClose = () => {
    if (executing) return;
    setOpen(false);
  };

  const handleExecute = async () => {
    setExecuting(true);
    setError(null);
    try {
      const res = await v2.admin.migrateFromV1({ dry_run: false });
      setResult(res.data);
      toast.success(
        `Migration complete — ${res.data.lines_exploded.length} lines moved to V2, `
        + `${res.data.v1_work_orders_cancelled.length} V1 work orders cancelled`,
      );
      if (onMigrated) onMigrated(res.data);
    } catch (err) {
      console.error('Migration failed', err);
      setError(err.response?.data?.error || 'Migration failed — nothing was changed');
      toast.error('Migration failed');
    } finally {
      setExecuting(false);
    }
  };

  const handleUndoClick = () => {
    setUndoConfirmOpen(true);
  };

  const handleUndoCancel = () => {
    if (undoing) return;
    setUndoConfirmOpen(false);
  };

  const handleUndoConfirm = async () => {
    setUndoing(true);
    try {
      const res = await v2.admin.undoMigration();
      const d = res.data || {};
      toast.success(
        `Undo complete — ${d.v2_wos_cancelled ?? 0} V2 work order(s) cancelled, `
        + `${d.v1_wos_restored ?? 0} V1 work order(s) restored, `
        + `${d.v1_reservations_restored ?? 0} reservation(s) re-activated`,
      );
      if (onUndone) onUndone(d);
      setUndoConfirmOpen(false);
    } catch (err) {
      console.error('Undo migration failed', err);
      const msg = err.response?.data?.error || 'Undo failed';
      toast.error(msg);
    } finally {
      setUndoing(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="warning"
        size="small"
        startIcon={<SwapHoriz />}
        onClick={handleOpen}
        data-testid="migrate-v1-button"
      >
        Migrate from V1
      </Button>

      <Button
        variant="outlined"
        color="warning"
        size="small"
        startIcon={<SettingsBackupRestore />}
        onClick={handleUndoClick}
        sx={{ ml: 1 }}
        data-testid="undo-migration-button"
      >
        Undo last migration
      </Button>

      <Dialog
        open={undoConfirmOpen}
        onClose={handleUndoCancel}
        maxWidth="sm"
        fullWidth
        data-testid="undo-migration-dialog"
      >
        <DialogTitle>Undo last V1 → V2 migration?</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
            Reverse the most recent V1 → V2 migration? This cancels all V2
            work orders from that run, restores the V1 work orders to their
            previous status, and re-activates the V1 component reservations
            released during the cutover.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Use this for testing — run the migration, exercise the shop
            floor, then undo to return to the V1 baseline before iterating.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleUndoCancel}
            disabled={undoing}
            data-testid="undo-migration-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleUndoConfirm}
            disabled={undoing}
            startIcon={undoing ? <CircularProgress size={16} /> : <SettingsBackupRestore />}
            data-testid="undo-migration-confirm"
          >
            {undoing ? 'Undoing…' : 'Undo migration'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth data-testid="migrate-dialog">
        <DialogTitle>
          Migrate from V1 — full cutover
        </DialogTitle>
        <DialogContent dividers>
          {!result && (
            <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
              This switches production to V2. Every <strong>active V1 work order is
              cancelled</strong> (its reserved materials are released back to stock) and
              fresh V2 work orders are created for whatever each open order still
              needs — anything already shipped or covered by allocated stock is not
              re-made. Order statuses and stock allocations are not touched.
            </Alert>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }} data-testid="migrate-loading">
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="migrate-error">
              {error}
            </Alert>
          )}

          {!loading && !result && report && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Dry run — nothing has been changed yet. Review and confirm below.
              </Typography>
              <ReportSummary report={report} />
            </>
          )}

          {result && (
            <Box data-testid="migrate-result">
              <Alert severity="success" sx={{ mb: 2 }}>
                Migration complete. {result.lines_exploded.length} line
                {result.lines_exploded.length === 1 ? '' : 's'} now have V2 work orders;
                {' '}{result.v1_work_orders_cancelled.length} V1 work order
                {result.v1_work_orders_cancelled.length === 1 ? '' : 's'} cancelled.
              </Alert>
              <ReportSummary report={result} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={executing} data-testid="migrate-close">
            {result ? 'Done' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleExecute}
              disabled={loading || executing || !report}
              startIcon={executing ? <CircularProgress size={16} /> : <SwapHoriz />}
              data-testid="migrate-execute"
            >
              {executing ? 'Migrating…' : 'Run migration'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
